import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import {
  ArrowLeft, Package, Pencil, Loader2, RotateCw,
  AlertTriangle, TrendingDown, Building2, Boxes
} from 'lucide-react'
import { apiGet, cachedGet } from '../../lib/api.js'
import { useMe } from '../../lib/me.js'
import { canManageInventory } from '../../lib/role.js'
import { relabelBranch } from '../../lib/ticket.js'
import BranchLevelBars from '../../components/stock/BranchLevelBars.jsx'
import StockUsageChart from '../../components/stock/StockUsageChart.jsx'
import InventoryEditDrawer from '../../components/stock/InventoryEditDrawer.jsx'
import {
  rows, imageUrl, typeTone, fmtTime, STOCK_TYPES,
} from '../../lib/stock.js'
import {
  RANGES, DEFAULT_RANGE_MONTHS, STAT_BRANCH, splitBranchStat, fillMonths,
  avgPerMonth, monthsOfCover, usageStatParams, itemName, belowMin, negativeBranches,
} from '../../lib/inventory.js'
import Pager from '../../components/Pager.jsx'

// One inventory item — everything the clinic knows about it.
//
// Three questions, in the order someone actually asks them:
//   1. เหลือเท่าไหร่ทั้งคลินิก?  → the clinic total (a stat tile)
//   2. เหลือที่สาขาไหนบ้าง?      → per-branch bars (สนามเป้า excluded — see below)
//   3. ใช้ไปเดือนละเท่าไหร่?     → monthly เบิกใช้, 1/3/6/12 เดือน (default 6)
// …and then the ledger itself, paginated, so a number that looks wrong can be
// traced to the rows that made it.
//
// Levels come from GET /stock/stat?stat_type=branch with NO type filter: every
// movement type nets out, so the sum IS the current level. Usage comes from the
// same endpoint in `time` mode filtered to เบิกใช้ — "used" means used, not
// transferred away.

const PER_PAGE = 25

function fmtDate(iso) {
  if (!iso) return '—'
  const [y, m, d] = String(iso).split('-')
  if (!y) return iso
  return `${Number(d)}/${Number(m)}/${String(y).slice(2)}`
}
const fmtN = (n) => Math.round(Number(n) || 0).toLocaleString('en-US')

export default function InventoryDetail() {
  const { id } = useParams()
  const inventoryId = Number(id) || 0
  const navigate = useNavigate()
  const me = useMe()
  const canEdit = canManageInventory(me)

  const [item, setItem] = useState(null)
  const [itemErr, setItemErr] = useState(null)
  const [branches, setBranches] = useState([])
  const [reloadKey, setReloadKey] = useState(0)

  const [branchStat, setBranchStat] = useState(null)
  const [statLoading, setStatLoading] = useState(true)

  const [months, setMonths] = useState(DEFAULT_RANGE_MONTHS)
  const [usage, setUsage] = useState(null)
  const [usageLoading, setUsageLoading] = useState(true)

  const [fBranch, setFBranch] = useState('')
  const [fType, setFType] = useState('')
  const [page, setPage] = useState(1)
  const [ledger, setLedger] = useState(null)
  const [ledgerLoading, setLedgerLoading] = useState(true)

  // UNFILTERED movement count — the delete guard's question is "does anything
  // reference this item at all", which the ledger's filtered total cannot
  // answer ("no rows matching เบิกใช้ at สาขา X" is not "no rows"). One cheap
  // per_page=1 call, so the UI's answer matches what the proxy will decide.
  const [totalMovements, setTotalMovements] = useState(null)
  const [editOpen, setEditOpen] = useState(false)

  const refresh = () => setReloadKey((k) => k + 1)

  /* ── the item ───────────────────────────────────────────────────────────── */
  useEffect(() => {
    let alive = true
    // `type=normal` is the MODE, not a filter — omitting it is a 500. See lib/stock.js.
    apiGet('/api/inventory', { type: 'normal', inventory_id: inventoryId, per_page: 1 })
      .then((r) => {
        if (!alive) return
        const found = rows(r).find((x) => Number(x?.id) === inventoryId) || rows(r)[0] || null
        if (!found) setItemErr('ไม่พบของชิ้นนี้')
        setItem(found)
      })
      .catch((e) => { if (alive) setItemErr(e.message || 'โหลดข้อมูลของไม่สำเร็จ') })
    return () => { alive = false }
  }, [inventoryId, reloadKey])

  useEffect(() => {
    cachedGet('/api/branches').then((r) => setBranches(rows(r))).catch(() => {})
  }, [])

  /* ── levels per branch (no `type` → every movement nets out) ────────────── */
  useEffect(() => {
    let alive = true
    setStatLoading(true)
    apiGet('/api/stock/stat', { inventory_id: inventoryId, stat_type: STAT_BRANCH })
      .then((r) => { if (alive) setBranchStat(r) })
      .catch(() => { if (alive) setBranchStat(null) })
      .finally(() => { if (alive) setStatLoading(false) })
    return () => { alive = false }
  }, [inventoryId, reloadKey])

  useEffect(() => {
    let alive = true
    apiGet('/api/stock', { inventory_id: inventoryId, page: 1, per_page: 1 })
      .then((r) => { if (alive) setTotalMovements(Number(r?.itemsTotal) || rows(r).length) })
      // A failed count must NOT read as zero — null keeps the delete button
      // hidden rather than offering a delete the proxy would refuse anyway.
      .catch(() => { if (alive) setTotalMovements(null) })
    return () => { alive = false }
  }, [inventoryId, reloadKey])

  /* ── monthly usage ──────────────────────────────────────────────────────── */
  useEffect(() => {
    let alive = true
    setUsageLoading(true)
    apiGet('/api/stock/stat', usageStatParams(inventoryId, months))
      .then((r) => { if (alive) setUsage(r) })
      .catch(() => { if (alive) setUsage(null) })
      .finally(() => { if (alive) setUsageLoading(false) })
    return () => { alive = false }
  }, [inventoryId, months, reloadKey])

  /* ── the ledger ─────────────────────────────────────────────────────────── */
  useEffect(() => { setPage(1) }, [fBranch, fType])

  useEffect(() => {
    let alive = true
    setLedgerLoading(true)
    apiGet('/api/stock', {
      inventory_id: inventoryId,
      branch_id: fBranch || undefined,
      type: fType || undefined,
      page,
      per_page: PER_PAGE,
    })
      .then((r) => { if (alive) setLedger(r) })
      .catch(() => { if (alive) setLedger(null) })
      .finally(() => { if (alive) setLedgerLoading(false) })
    return () => { alive = false }
  }, [inventoryId, fBranch, fType, page, reloadKey])

  const branchNameById = useMemo(() => {
    const m = {}
    for (const b of branches) m[Number(b.id)] = b.name || b.Name
    return m
  }, [branches])

  const { visible: branchRows, legacy, total } = useMemo(
    () => splitBranchStat(branchStat, branchNameById), [branchStat, branchNameById])

  const chartRows = useMemo(() => branchRows.map((r) => ({
    key: r.branch_id, label: relabelBranch(r.label), value: r.value,
  })), [branchRows])

  // สนามเป้า is checked too: it is off the chart, but a negative there is the
  // same broken ledger and still drags the clinic total down.
  const negatives = useMemo(
    () => negativeBranches([...chartRows, ...(legacy ? [{ key: legacy.branch_id, label: relabelBranch(legacy.label), value: legacy.value }] : [])]),
    [chartRows, legacy])

  const points = useMemo(() => fillMonths(usage, months), [usage, months])
  const avg = useMemo(() => avgPerMonth(points), [points])
  const cover = useMemo(() => monthsOfCover(total, points), [total, points])

  const unit = item?.unit || ''
  const low = belowMin(total, item)
  const ledgerRows = rows(ledger)
  const ledgerTotal = Number(ledger?.itemsTotal) || 0
  const pageTotal = Number(ledger?.pageTotal) || 1

  if (itemErr) {
    return (
      <div className="px-10 py-8 max-w-[1150px]">
        <BackLink />
        <div className="mt-6 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-800">{itemErr}</div>
      </div>
    )
  }

  return (
    <div className="px-10 py-8 max-w-[1150px]">
      <BackLink />

      {/* ── identity ─────────────────────────────────────────────────────── */}
      <div className="mt-4 mb-6 flex items-start gap-5">
        <div className="w-20 h-20 rounded-2xl border border-slate-200 bg-white overflow-hidden shrink-0 flex items-center justify-center">
          {imageUrl(item)
            ? <img src={imageUrl(item)} alt="" className="w-full h-full object-cover" />
            : <Package size={26} className="text-slate-300" />}
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-[22px] font-semibold text-slate-900 tracking-tight truncate">
            {item ? itemName(item) : 'กำลังโหลด…'}
          </h1>
          <div className="mt-1 text-[13.5px] text-slate-500">
            {[item?.mainCategory, item?.subCategory].filter(Boolean).join(' · ') || '—'}
            {unit && <> · หน่วย <span className="text-slate-700">{unit}</span></>}
            <span className="text-slate-300"> · #{inventoryId}</span>
          </div>
          {item?.note && <p className="mt-1.5 text-[13px] text-slate-500">{item.note}</p>}
          {item && item.active === false && (
            <span className="mt-2 inline-block rounded-full bg-slate-100 px-2.5 py-0.5 text-[11.5px] text-slate-500">ปิดใช้งาน</span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={refresh} title="โหลดใหม่"
            className="rounded-lg border border-slate-200 bg-white p-2 text-slate-500 hover:text-brand-600 hover:border-brand-300">
            <RotateCw size={15} />
          </button>
          {canEdit && item && (
            <button onClick={() => setEditOpen(true)}
              className="rounded-xl bg-brand-600 px-4 py-2.5 text-[14px] font-semibold text-white hover:bg-brand-700 flex items-center gap-2">
              <Pencil size={15} />แก้ไข
            </button>
          )}
        </div>
      </div>

      {/* ── the numbers ──────────────────────────────────────────────────── */}
      <div className="grid gap-3 sm:grid-cols-3 mb-4">
        {/* Three tones, three different meanings — never collapsed together:
            danger = the ledger disagrees with reality (negative somewhere),
            warn   = the count is real and has hit the reorder point,
            default= fine. A negative outranks a low count: there is no point
            reordering against a number we know is wrong. */}
        <Stat
          icon={Boxes}
          label="คงเหลือทั้งคลินิก"
          value={statLoading ? null : fmtN(total)}
          suffix={unit}
          tone={negatives.length ? 'danger' : low ? 'warn' : 'default'}
          hint={
            negatives.length
              ? `มี ${negatives.length} สาขาที่ติดลบ — ยอดรวมนี้ยังเชื่อไม่ได้`
              : low ? `ถึงจุดสั่งซื้อแล้ว (ขั้นต่ำ ${fmtN(item?.min)})` : null
          }
        />
        <Stat
          icon={TrendingDown}
          label={`เบิกใช้เฉลี่ย / เดือน (${months === 12 ? '1 ปี' : `${months} เดือน`})`}
          value={usageLoading ? null : fmtN(avg)}
          suffix={unit}
          hint={points.length > 1 ? 'ไม่รวมเดือนปัจจุบันที่ยังไม่จบ' : null}
        />
        <Stat
          icon={Building2}
          label="ใช้ได้อีกประมาณ"
          value={statLoading || usageLoading ? null : (cover == null ? '—' : cover.toFixed(1))}
          suffix={cover == null ? '' : 'เดือน'}
          hint={cover == null ? 'ยังไม่มีประวัติการเบิกใช้พอจะคำนวณ' : null}
        />
      </div>

      {/* ── per branch ───────────────────────────────────────────────────── */}
      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-[14.5px] font-semibold text-slate-800">คงเหลือแยกตามสาขา</h2>
          <span className="text-[12px] text-slate-400">รวมทุกประเภทการเคลื่อนไหว</span>
        </div>
        {/* A negative level is a MISSING ROW, not a shortage — physical stock
            cannot be less than nothing. Named branches + the likely causes,
            because "−6" alone tells nobody what to do about it. */}
        {!statLoading && negatives.length > 0 && (
          <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
            <div className="flex items-start gap-2.5">
              <AlertTriangle size={16} className="text-rose-600 shrink-0 mt-0.5" />
              <div className="text-[13px] text-rose-800">
                <div className="font-semibold">ยอดคงเหลือติดลบ — ข้อมูลไม่ตรงกับของจริง</div>
                <div className="mt-1">
                  {negatives.map((r) => (
                    <span key={r.key} className="inline-block mr-3">
                      {r.label} <b className="tabular-nums">{fmtN(r.value)}</b> {unit}
                    </span>
                  ))}
                </div>
                <div className="mt-1.5 text-[12.5px] text-rose-700/90">
                  ของจริงติดลบไม่ได้ — แปลว่ามีรายการที่ยังไม่ได้บันทึก เช่น เบิกใช้แล้วแต่ยังไม่ได้ลง “รับเพิ่ม” ตอนของเข้า ·
                  โอนย้ายที่บันทึกด้านเดียว · หรือเคยนับของแล้วยังไม่ได้ลง “ปรับ”
                  <br />แก้โดยเพิ่มรายการที่ขาด (ไม่ใช่สั่งของเพิ่ม) — ดูตารางการเคลื่อนไหวด้านล่าง
                </div>
              </div>
            </div>
          </div>
        )}
        {statLoading
          ? <div className="h-[180px] flex items-center justify-center text-[13px] text-slate-400">
              <Loader2 size={16} className="animate-spin mr-2" />กำลังโหลด…
            </div>
          : <BranchLevelBars data={chartRows} unit={unit} emptyText="ยังไม่มีการเคลื่อนไหวของของชิ้นนี้" />}
        {/* สนามเป้า is excluded from the bars (legacy branch, hidden everywhere
            else in the app) — but if it still holds stock, say so, so the bars
            and the clinic total above reconcile instead of quietly disagreeing. */}
        {legacy && (
          <p className="mt-3 border-t border-slate-100 pt-2.5 text-[12px] text-slate-400">
            ไม่รวมในกราฟ: {relabelBranch(legacy.label)} คงเหลือ{' '}
            <b className="text-slate-600 tabular-nums">{fmtN(legacy.value)}</b> {unit} (สาขาเดิม — นับรวมในยอดทั้งคลินิกแล้ว)
          </p>
        )}
      </section>

      {/* ── usage over time ──────────────────────────────────────────────── */}
      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 mb-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h2 className="text-[14.5px] font-semibold text-slate-800">เบิกใช้รายเดือน</h2>
          <div className="flex gap-1.5">
            {RANGES.map((r) => (
              <button key={r.months} onClick={() => setMonths(r.months)}
                className={`rounded-lg border px-2.5 py-1.5 text-[12.5px] transition ${
                  months === r.months
                    ? 'border-brand-300 bg-brand-50 text-brand-700 font-medium'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-brand-300'
                }`}>
                {r.label}
              </button>
            ))}
          </div>
        </div>
        <StockUsageChart points={points} unit={unit} loading={usageLoading} avg={avg} />
      </section>

      {/* ── the ledger ───────────────────────────────────────────────────── */}
      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-3.5">
          <h2 className="text-[14.5px] font-semibold text-slate-800 mr-auto">การเคลื่อนไหว</h2>
          <select value={fBranch} onChange={(e) => setFBranch(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[13px] text-slate-800">
            <option value="">ทุกสาขา</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>{relabelBranch(b.name || b.Name) || `สาขา ${b.id}`}</option>
            ))}
          </select>
          <select value={fType} onChange={(e) => setFType(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[13px] text-slate-800">
            <option value="">ทุกประเภท</option>
            {STOCK_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11.5px] font-medium uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="px-5 py-3 font-medium">วันที่</th>
                <th className="px-5 py-3 font-medium">สาขา</th>
                <th className="px-5 py-3 font-medium">ประเภท</th>
                <th className="px-5 py-3 font-medium text-right">จำนวน</th>
                <th className="px-5 py-3 font-medium">หมายเหตุ</th>
                <th className="px-5 py-3 font-medium">ผู้บันทึก</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {ledgerLoading && (
                <tr><td colSpan={6} className="px-5 py-14 text-center text-slate-400">
                  <Loader2 size={18} className="inline animate-spin mr-2" /> กำลังโหลด…
                </td></tr>
              )}
              {!ledgerLoading && !ledgerRows.length && (
                <tr><td colSpan={6} className="px-5 py-14 text-center text-slate-400">ไม่พบรายการเคลื่อนไหว</td></tr>
              )}
              {!ledgerLoading && ledgerRows.map((r) => {
                const n = Number(r?.N) || 0
                return (
                  <tr key={r.id} className="hover:bg-slate-50/60">
                    <td className="px-5 py-3 align-top whitespace-nowrap text-slate-500 tabular-nums">
                      <div>{fmtDate(r.date)}</div>
                      <div className="text-[11.5px] text-slate-400">{fmtTime(r.Creation_Date)}</div>
                    </td>
                    <td className="px-5 py-3 align-top whitespace-nowrap">
                      <span className="inline-flex items-center gap-1.5 text-slate-600">
                        {r?.branch?.color && <span className="w-2 h-2 rounded-full shrink-0" style={{ background: r.branch.color }} />}
                        {relabelBranch(r?.branch?.name) || `#${r.branch_id}`}
                      </span>
                    </td>
                    <td className="px-5 py-3 align-top whitespace-nowrap">
                      <span className={`inline-block rounded-full border px-2 py-0.5 text-[11.5px] font-medium ${typeTone(r.type)}`}>
                        {r.type || '—'}
                      </span>
                    </td>
                    <td className={`px-5 py-3 align-top text-right tabular-nums font-semibold whitespace-nowrap ${
                      n < 0 ? 'text-rose-600' : 'text-emerald-600'
                    }`}>
                      {n > 0 ? `+${n}` : n}
                    </td>
                    <td className="px-5 py-3 align-top text-slate-500 max-w-[260px] truncate" title={r.remark || ''}>
                      {r.remark || '—'}
                    </td>
                    <td className="px-5 py-3 align-top text-slate-500 whitespace-nowrap">{r.Creator || '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between gap-4 border-t border-slate-100 px-5 py-3 text-[13px] text-slate-500">
          <div>ทั้งหมด <b className="text-slate-700 tabular-nums">{fmtN(ledgerTotal)}</b> รายการ</div>
          <Pager page={page} pageTotal={pageTotal} onChange={setPage} disabled={ledgerLoading} />
        </div>
      </section>

      {editOpen && item && (
        <InventoryEditDrawer
          item={item}
          movementCount={totalMovements}
          onClose={() => setEditOpen(false)}
          onSaved={refresh}
          onDeleted={() => navigate('/stock/items', { replace: true })}
        />
      )}
    </div>
  )
}

function BackLink() {
  return (
    <Link to="/stock/items" className="inline-flex items-center gap-1.5 text-[13.5px] text-slate-500 hover:text-brand-600">
      <ArrowLeft size={15} />คลังของ
    </Link>
  )
}

const STAT_TONES = {
  default: { box: 'border-slate-200 bg-white',   num: 'text-slate-900', hint: 'text-slate-400' },
  warn:    { box: 'border-amber-200 bg-amber-50', num: 'text-amber-900', hint: 'text-amber-700' },
  danger:  { box: 'border-rose-200 bg-rose-50',   num: 'text-rose-900',  hint: 'text-rose-700' },
}

function Stat({ icon: Icon, label, value, suffix, hint, tone = 'default' }) {
  const t = STAT_TONES[tone] || STAT_TONES.default
  return (
    <div className={`rounded-2xl border p-4 shadow-sm ${t.box}`}>
      <div className="flex items-center gap-1.5 text-[12px] font-medium uppercase tracking-wide text-slate-400 mb-1.5">
        <Icon size={13} />{label}
      </div>
      <div className="flex items-baseline gap-1.5">
        {value == null
          ? <Loader2 size={18} className="animate-spin text-slate-300" />
          : <span className={`text-[26px] font-semibold tabular-nums ${t.num}`}>{value}</span>}
        {suffix && <span className="text-[13px] text-slate-400">{suffix}</span>}
      </div>
      {hint && (
        <p className={`mt-1 text-[11.5px] flex items-start gap-1 ${t.hint}`}>
          {tone !== 'default' && <AlertTriangle size={12} className="shrink-0 mt-0.5" />}{hint}
        </p>
      )}
    </div>
  )
}
